import SesamLink from 'Common/Links/SesamLink';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import get from 'lodash/get';

import { SubscriptionCardContainer } from 'src/containers/SubscriptionCard.container';
import Button from 'Common/Button/Button';
import { CallToActionCard } from '../../components/call-to-action-card/CallToActionCard.view';
import { CardListDataTable } from '../../components/card-list-data-table/CardListDataTable';
import { MainLayoutContainer } from '../../containers/MainLayout.container';
import Page from '../../components/page';
import PageHeader, { PageHeaderTitle } from '../../components/page-header';
import { SubActions } from 'Redux/thunks/subscriptions.thunks';
import SubscriptionButtons from '../../components/subscription-buttons';
import { IdentityIcon } from 'src/components/identity-icon/IdentityIcon.view';
import { findDefaultUrl } from '../../utils/subscription.utils';
import { toastAdded } from 'Redux/thunks/global.thunks';
import type { Subscription } from 'Types/subscription.types';
import type { License } from 'Types/subscription.types';
import type { RootState } from 'Types/state.types';
import type { PaymentMethodCardProps, SomeObject } from 'Types/common.types';
import { CustomConnectionsThunk } from 'Redux/thunks/customConnectionURL.thunks';
import { TestID } from '../../testID';

export const DashboardPage = () => {
  function createdSorter(_col: SomeObject, row1: SomeObject, row2: SomeObject) {
    const r1 = get(row1, ['sub', 'created']);
    const r2 = get(row2, ['sub', 'created']);

    return new Date(r1).getTime() - new Date(r2).getTime();
  }

  function getDescription(dataItem: SomeObject) {
    return dataItem.description || (dataItem.sub && dataItem.sub.description);
  }

  function getIcon(dataItem: SomeObject) {
    return dataItem.sub && <IdentityIcon input={dataItem.sub.name} size={24} />;
  }

  function getLicenseExpiry(dataItem: SomeObject) {
    if (dataItem.licenses) {
      return dataItem.licenses.find((license: License) => license['is-active-license']).original
        .expires;
    }
    return '';
  }

  function getSubActions(dataItem: SomeObject) {
    return dataItem.actions || <SubscriptionButtons sub={dataItem.sub} />;
  }

  function getSubName(dataItem: SomeObject) {
    if (dataItem.sub && dataItem.sub.id) {
      return (
        <SesamLink
          title={findDefaultUrl(dataItem.sub) + '\n' + dataItem.sub.name}
          to={`/subscription/${dataItem.sub.id}`}
        >
          {dataItem.sub.name}
        </SesamLink>
      );
    }
    return dataItem.name;
  }

  function subSorter(_col, row1, row2) {
    const name1 = row1.sub ? row1.sub.name : row1.name;
    const name2 = row2.sub ? row2.sub.name : row2.name;

    if (name1 > name2) {
      return 1;
    } else if (name1 < name2) {
      return -1;
    }

    return 0;
  }

  const [emailVerificationNotified, setEmailVerificationNotified] = useState(false);

  const subscriptions = useSelector((state: RootState) => state.subscriptions);
  const customConnectionURLs = useSelector((state: RootState) => state.customConnectionURLs);
  const emailVerified = useSelector((state: RootState) => state.user.emailVerified);

  const dispatch = useDispatch();
  const reloadSubs = () => dispatch(SubActions.loadAll());
  const isItestMode = useSelector((state: RootState) => state.globals.status.is_itest_mode);
  const addToast = (message: string) => dispatch(toastAdded({ message, type: 'warning' }));

  useEffect(() => {
    dispatch(CustomConnectionsThunk.setAllCustomConnections());
  }, [CustomConnectionsThunk.setAllCustomConnections, dispatch]);

  useEffect(() => {
    reloadSubs();
  }, [reloadSubs]);

  useEffect(() => {
    if (emailVerified === false && !emailVerificationNotified && !isItestMode) {
      addToast(
        'Your email is not verified. We have sent a verification email to your email address. To resend the verification email, go to your user profile'
      );
      setEmailVerificationNotified(true);
    }
  }, [addToast, emailVerified, emailVerificationNotified, isItestMode]);

  const pinnedItems: PaymentMethodCardProps[] = useMemo(() => {
    const pinned = [
      {
        id: 'new-sub',
        renderer: CallToActionCard,
        name: 'New subscription',
        tagline: 'On-cloud or on-premise',
        description:
          'Create a fully-configurable, ongoing Sesam subscription. You need a verified payment method.',
        actions: [
          <SesamLink key={crypto.randomUUID()} to="/create-subscription" buttonLink={true}>
            <Button theme="primary">New subscription</Button>
          </SesamLink>,
        ],
        asideActions: [
          <SesamLink
            key={crypto.randomUUID()}
            to="/user/payment-methods"
            testId={TestID.NewSubscriptionCardPaymentMethodsLink}
          >
            Payment methods
          </SesamLink>,
        ],
      },
    ];

    return pinned;
  }, []);

  const data: PaymentMethodCardProps[] = pinnedItems.concat(
    subscriptions.map((sub: Subscription) => {
      return {
        renderer: SubscriptionCardContainer,
        id: sub.id,
        sub,
        customConnectionURL: customConnectionURLs[sub.id],
        testId: `${TestID.DashboardSubscriptionCard}-${sub.id}`,
      };
    })
  );

  return (
    <MainLayoutContainer>
      <Page>
        <PageHeader>
          <PageHeaderTitle data-testid={TestID.DashboardPageTitle}>Dashboard</PageHeaderTitle>
        </PageHeader>
        <main className="scrollArea">
          <CardListDataTable
            cols={[
              { header: '', type: 'icon', data: getIcon, sorter: null },
              {
                header: 'Subscription',
                data: getSubName,
                sorter: subSorter,
                type: 'medium-text',
              },
              {
                header: 'Description',
                data: getDescription,
                type: 'long-text',
              },
              {
                header: 'Actions',
                type: 'actions',
                data: getSubActions,
                sorter: null,
              },
            ]}
            data={data}
            dataKey="id"
            fields={[
              {
                header: 'Created date',
                data: 'created',
                sorter: createdSorter,
              },
              { header: 'License expiry', data: getLicenseExpiry },
            ]}
            id="dashboard"
            pinnedIds={pinnedItems.map((pin) => pin.id)}
            sortable
            defaultSortByKey="Subscription"
          />
        </main>
      </Page>
    </MainLayoutContainer>
  );
};
